/**
 * Leave the in-app browser (KakaoTalk, Zalo, Facebook, Messenger, Instagram, LINE, NAVER, BAND).
 * Mounted once in the root route.
 *
 * - KakaoTalk, shared entry pages (lib/in-app-browser `isKakaoAutoExitPath`): on the first load of
 *   the session the page hands itself to the device's default browser (`kakaotalk://web/openExternal`)
 *   — not while this WebView holds a scorer session or unsent caddy ops (scorer-guard.ts). If the
 *   page is still here after KAKAO_EXIT_WAIT_MS (blocked, or the user came back) the banner shows.
 * - Everywhere else: the banner only. It sits in normal flow at the top of every page, so it pushes
 *   content down instead of covering the caddy sync bar or the Review & save bar.
 *
 * Never in TV mode; dismissible for the session; `?ext=0` opts out of both. While caddy ops are
 * queued the "Open in browser" button waits for the queue to empty (scorer-guard.ts explains why).
 */
import { useRouterState } from '@tanstack/react-router';
import { ExternalLink, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '../ui';
import {
  IN_APP_NAMES,
  KAKAO_EXIT_WAIT_MS,
  canAutoExit,
  detectInAppBrowser,
  type InAppBrowser,
  externalOpen,
  hasOptOut,
  isCaddyPath,
  isInAppBannerExcluded,
  isInAppDismissed,
  kakaoExitTried,
  kakaoExternalUrl,
  markKakaoExitTried,
  rememberInAppDismissed,
} from '../../lib/in-app-browser';
import { countPendingOps, holdsScorerSession, usedForScoring } from './scorer-guard';
import './in-app-banner.css';

export interface InAppBannerProps {
  /** defaults to navigator.userAgent (tests pass one) */
  userAgent?: string;
  /** defaults to navigator.maxTouchPoints (iPadOS detection) */
  maxTouchPoints?: number;
  /** opens the external-browser href (tests pass a spy; default = a link activation) */
  navigate?: (href: string) => void;
  /** unsent caddy ops (tests may inject; default = the caddy IndexedDB queue) */
  pendingOps?: () => Promise<number>;
}

/** how often the queue is re-read while the banner can send the user away */
const PENDING_POLL_MS = 3000;

/**
 * Activate a link instead of replacing location: the host WebView sees a normal link tap
 * (shouldOverrideUrlLoading with a gesture) for `intent://` and app schemes.
 */
function activateLink(href: string) {
  const a = document.createElement('a');
  a.href = href;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function initiallyDismissed(): boolean {
  if (typeof window !== 'undefined' && hasOptOut(window.location.search)) {
    rememberInAppDismissed();
    return true;
  }
  return isInAppDismissed();
}

type Phase = 'checking' | 'waiting' | 'idle';

/** Latest value in a ref, for effects that run once but must call the current prop. */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * KakaoTalk: leave automatically once per session, on the first load only (later route changes
 * never redirect). Returns 'idle' once the banner may show.
 */
function useKakaoAutoExit(
  browser: InAppBrowser | null,
  pathname: string,
  navigate: (href: string) => void,
  pendingOps: () => Promise<number>,
): Phase {
  const [phase, setPhase] = useState<Phase>(() =>
    typeof window !== 'undefined' && canAutoExit(browser, pathname, window.location.search)
      ? 'checking'
      : 'idle',
  );
  const navigateRef = useLatest(navigate);
  const pendingRef = useLatest(pendingOps);
  const [startPath] = useState(pathname);

  useEffect(() => {
    if (phase !== 'checking') return;
    let cancelled = false;
    let timer: number | undefined;
    void (async () => {
      let blocked = holdsScorerSession();
      if (!blocked && (isCaddyPath(startPath) || usedForScoring())) {
        blocked = (await pendingRef.current().catch(() => 0)) > 0;
      }
      if (cancelled) return;
      if (blocked || kakaoExitTried()) {
        setPhase('idle');
        return;
      }
      markKakaoExitTried();
      navigateRef.current(kakaoExternalUrl(window.location.href));
      setPhase('waiting');
      timer = window.setTimeout(() => setPhase('idle'), KAKAO_EXIT_WAIT_MS);
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // runs for the 'checking' phase only (set once at mount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return phase;
}

/** Unsent caddy ops while `watch` is on (re-read every PENDING_POLL_MS), else 0. */
function usePendingOps(watch: boolean, pendingOps: () => Promise<number>): number {
  const [pending, setPending] = useState(0);
  const pendingRef = useLatest(pendingOps);
  useEffect(() => {
    if (!watch) {
      setPending(0);
      return;
    }
    let alive = true;
    const read = () =>
      void pendingRef
        .current()
        .catch(() => 0)
        .then((n) => {
          if (alive) setPending(n);
        });
    read();
    const timer = window.setInterval(read, PENDING_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [watch, pendingRef]);
  return pending;
}

/**
 * Copy the page link. Many in-app WKWebViews (Zalo, Instagram, NAVER) have no clipboard API or
 * reject it: then the URL is shown in a read-only field, selected, execCommand('copy') is tried
 * and a hint says to press and hold it.
 */
function useCopyLink() {
  const [copied, setCopied] = useState(false);
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    const input = urlRef.current;
    if (!manualUrl || !input) return;
    input.focus();
    input.select();
    try {
      if (typeof document.execCommand === 'function' && document.execCommand('copy')) {
        setCopied(true);
      }
    } catch {
      /* the hint tells the user to copy it by hand */
    }
  }, [manualUrl]);

  const copy = async () => {
    const url = window.location.href;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setManualUrl(null);
      setCopied(true);
    } catch {
      setCopied(false);
      setManualUrl(url);
    }
  };

  return { copied, manualUrl, urlRef, copy };
}

export function InAppBanner({
  userAgent,
  maxTouchPoints,
  navigate = activateLink,
  pendingOps = countPendingOps,
}: InAppBannerProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  const touch = maxTouchPoints ?? (typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints);
  const browser = detectInAppBrowser(ua, touch);

  // decided before the dismissal below records ?ext=0
  const phase = useKakaoAutoExit(browser, pathname, navigate, pendingOps);
  const [dismissed, setDismissed] = useState(initiallyDismissed);
  const visible = !!browser && !dismissed && phase === 'idle' && !isInAppBannerExcluded(pathname);
  const caddy = isCaddyPath(pathname);
  const pending = usePendingOps(visible && (caddy || usedForScoring()), pendingOps);

  if (!browser || !visible) return null;
  return (
    <Banner
      browser={browser}
      caddy={caddy}
      pending={pending}
      navigate={navigate}
      onDismiss={() => {
        rememberInAppDismissed();
        setDismissed(true);
      }}
    />
  );
}

function Banner({
  browser,
  caddy,
  pending,
  navigate,
  onDismiss,
}: {
  browser: InAppBrowser;
  caddy: boolean;
  pending: number;
  navigate: (href: string) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('common');
  const [steps, setSteps] = useState<'ios' | 'android' | null>(null);
  const { copied, manualUrl, urlRef, copy } = useCopyLink();
  const titleId = useId();
  const app = IN_APP_NAMES[browser.kind];
  const blocked = pending > 0;
  const fallback = steps !== null && !blocked;

  const open = () => {
    if (blocked) return;
    const action = externalOpen(browser, window.location.href);
    if (action.type === 'instructions') {
      setSteps('ios');
      return;
    }
    navigate(action.href);
    // a host WebView that ignores intent:// leaves the user here: offer the manual way at once
    if (action.href.startsWith('intent:')) setSteps('android');
  };

  return (
    <aside
      className={caddy ? 'inapp-banner inapp-banner--caddy' : 'inapp-banner'}
      aria-labelledby={titleId}
      data-contrast={caddy ? 'outdoor' : undefined}
      data-testid="inapp-banner"
    >
      <div className="inapp-banner__body">
        <p className="inapp-banner__title" id={titleId}>
          {caddy ? t('inApp.caddyTitle') : t('inApp.title')}
        </p>
        <p className="inapp-banner__text">
          {caddy ? t('inApp.caddyBody', { app }) : t('inApp.body', { app })}
        </p>
        {blocked && (
          <p className="inapp-banner__pending" aria-live="polite">
            {t('inApp.pendingNote', { app, count: pending })}
          </p>
        )}
        {fallback && (
          <p className="inapp-banner__steps" role="status">
            {steps === 'ios' ? t('inApp.iosSteps', { app }) : t('inApp.androidSteps', { app })}
          </p>
        )}
        <div className="inapp-banner__actions">
          <Button
            variant="primary"
            className="inapp-banner__open"
            icon={<ExternalLink size={18} aria-hidden="true" />}
            disabled={blocked}
            onClick={open}
          >
            {blocked ? t('inApp.pendingFirst', { count: pending }) : t('inApp.open')}
          </Button>
          {fallback && (
            <Button className="inapp-banner__copy" onClick={() => void copy()}>
              {copied ? t('inApp.copied') : t('inApp.copy')}
            </Button>
          )}
        </div>
        {fallback && manualUrl && (
          <div className="inapp-banner__manual">
            <input
              ref={urlRef}
              className="inapp-banner__url"
              type="url"
              readOnly
              value={manualUrl}
              aria-label={t('inApp.linkLabel')}
              onFocus={(e) => e.currentTarget.select()}
            />
            <p className="inapp-banner__hint">{t('inApp.copyManual')}</p>
          </div>
        )}
      </div>
      <IconButton
        className="inapp-banner__close"
        label={t('inApp.dismiss')}
        icon={<X size={20} aria-hidden="true" />}
        onClick={onDismiss}
      />
    </aside>
  );
}
